// #93 RankedList (debrief D-8): the Alternates, ranked. A header with his words and the one pill *Active until primary
// accepts* (info; warn once read-only), then an ordered list of person rows, each with its rank Count and small up /
// down icon buttons — no drag. Each row is one tab stop: the row's two buttons rove with ←/→, and a move keeps focus
// on the moved row's button while a polite live line says the new order. Read-only after the primary accepts
// (`mode="view"`); `blocked` keeps the buttons focusable, aria-disabled and described by the reason.

import { useRef, useState } from "react";
import { cx } from "../lib/cx";
import { useRoving } from "../lib/useRoving";
import { useSvgId } from "../lib/useSvgId";
import { DEBRIEF_OUTCOME_LABELS } from "../labels";
import { IconButton, type Blocked } from "../core/Button";
import { Count } from "../core/Count";
import { Person } from "../core/Person";
import { Pill } from "../core/Pill";
import { SectionTitle } from "../core/Text";
import { DEBRIEF_ROOM_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface RankedItem {
  key: string;
  name: string;
  sub?: string | undefined;
  /** A candidate draws the rounded-square tile on its tint. */
  candidate?: boolean | undefined;
  tint?: `var(--${string})` | undefined;
  hueKey?: string | undefined;
}

export interface RankedListProps {
  items: readonly RankedItem[];
  /** Controlled order; without it the list keeps its own. */
  onReorder?: ((items: RankedItem[]) => void) | undefined;
  /** The heading's words. Default *Alternates*. */
  title?: string | undefined;
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  /** Hotspots: [up, down] per row key. */
  hs?: Partial<Record<string, readonly [Hs | undefined, Hs | undefined]>> | undefined;
  className?: string | undefined;
}

type Dir = -1 | 1;

function RankRow({ item, rank, count, edit, blocked, onMove, register, hs }: {
  item: RankedItem; rank: number; count: number; edit: boolean; blocked: Blocked | undefined;
  onMove: (dir: Dir) => void; register: (dir: Dir, element: HTMLButtonElement | null) => void;
  hs: readonly [Hs | undefined, Hs | undefined] | undefined;
}) {
  const up = rank > 1;
  const down = rank < count;
  const roving = useRoving({ count: 2, orientation: "horizontal", wrap: false, isDisabled: (at) => (at === 0 ? !up : !down) });
  const button = (at: 0 | 1) => {
    const dir: Dir = at === 0 ? -1 : 1;
    const own = roving.item(at);
    // The gap still fills its roving slot (as null), so the row's key handler never meets a hole in its list.
    if (at === 0 ? !up : !down) return <span ref={() => own.ref(null)} className="mc-rank-gap" aria-hidden="true" />;
    return (
      <IconButton
        sm
        icon={at === 0 ? "chevron-up" : "down"}
        className={cx(at === 0 && "mc-rank-up")}
        label={`${item.name} ${at === 0 ? "↑" : "↓"}`}
        tabIndex={own.tabIndex}
        ref={(element) => { own.ref(element); register(dir, element); }}
        onFocus={own.onFocus}
        onClick={() => onMove(dir)}
        blocked={blocked}
        hs={hs?.[at]}
      />
    );
  };
  return (
    <li className="mc-rank-r">
      <Count n={rank} />
      <Person
        name={item.name}
        sub={item.sub}
        size={24}
        candidate={item.candidate}
        tint={item.tint}
        hueKey={item.hueKey}
      />
      {edit ? <span className="mc-rank-b" {...roving.container}>{button(0)}{button(1)}</span> : null}
    </li>
  );
}

export function RankedList({ items, onReorder, title = DEBRIEF_OUTCOME_LABELS.alternates, can, blocked, mode, hs, className }: RankedListProps) {
  const id = useSvgId();
  const [own, setOwn] = useState<readonly RankedItem[] | null>(null);
  const [said, setSaid] = useState("");
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  if (can === false) return null;
  const order = onReorder ? items : own ?? items;
  const edit = mode !== "view";
  const move = (from: number, dir: Dir) => {
    const to = from + dir;
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    if (onReorder) onReorder(next);
    else setOwn(next);
    setSaid(`${moved.name} · ${to + 1}`);
    // Focus stays on the moved row: the same direction while it can still go, else the other button.
    const atEdge = to === 0 || to === next.length - 1;
    const keep = atEdge ? (-dir as Dir) : dir;
    requestAnimationFrame(() => buttons.current.get(`${moved.key}:${keep}`)?.focus());
  };
  const headId = `${id}-h`;
  return (
    <div className={cx("mc-ranked", className)}>
      <div className="mc-ranked-h">
        <SectionTitle as="h4" id={headId}>{title}</SectionTitle>
        <Pill tone={edit ? "info" : "warn"}>{DEBRIEF_ROOM_LABELS.activeUntilPrimaryAccepts}</Pill>
      </div>
      <ol className="mc-rank" aria-labelledby={headId}>
        {order.map((item, at) => (
          <RankRow
            key={item.key}
            item={item}
            rank={at + 1}
            count={order.length}
            edit={edit}
            blocked={blocked}
            onMove={(dir) => move(at, dir)}
            register={(dir, element) => {
              if (element) buttons.current.set(`${item.key}:${dir}`, element);
              else buttons.current.delete(`${item.key}:${dir}`);
            }}
            hs={hs?.[item.key]}
          />
        ))}
      </ol>
      {edit ? <span className="mc-sr" role="status" aria-live="polite">{said}</span> : null}
    </div>
  );
}
